import { useCallback, useEffect, useState } from 'react'

export type ToastVariant = 'default' | 'destructive' | 'success' | 'warning'

export interface Toast {
  id: string
  title?: string
  description?: string
  variant?: ToastVariant
  duration?: number
}

interface ToastState {
  toasts: Toast[]
}

let toastListeners: ((toasts: Toast[]) => void)[] = []
let toasts: Toast[] = []

export function toast(options: Omit<Toast, 'id'>) {
  const id = Math.random().toString(36).slice(2)
  toasts = [...toasts, { id, ...options }]
  toastListeners.forEach((listener) => listener([...toasts]))

  setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id)
    toastListeners.forEach((listener) => listener([...toasts]))
  }, options.duration || 5000)
}

export function useToast() {
  const [state, setState] = useState<ToastState>({ toasts: [] })

  useEffect(() => {
    const listener = (newToasts: Toast[]) => setState({ toasts: newToasts })
    toastListeners.push(listener)
    return () => {
      toastListeners = toastListeners.filter((l) => l !== listener)
    }
  }, [])

  const dismiss = useCallback((id: string) => {
    toasts = toasts.filter((t) => t.id !== id)
    toastListeners.forEach((listener) => listener([...toasts]))
  }, [])

  return { toasts: state.toasts, dismiss, toast }
}